import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Clock, CheckCircle, Package, Utensils, ChefHat, CreditCard, Banknote, Check } from 'lucide-react'
import { motion } from 'framer-motion'
import Logo from '@/components/Logo'
import { getTheme, MENU_THEMES } from '@/config/menuThemes'
import { getAssetUrl } from '@/config/api'
import type { MenuTheme, MenuThemeId } from '@/types'

interface OrderItem {
  id: string
  productName?: string
  name?: string
  quantity: number
  unitPrice: number
  itemTotal: number
  customizations?: any
  product?: {
    name?: string
  }
}

// Helper to get item name from different possible fields
const getItemName = (item: OrderItem): string =>
  item.productName || item.product?.name || item.name || 'Prodotto'

interface Order {
  id: string
  customerId: string
  customerName: string
  status: string
  items: OrderItem[]
  subtotal: number
  tax: number
  total: number
  notes?: string
  paymentMethod: string
  paymentStatus: string
  tableNumber: string
  restaurantName: string
  restaurantLogoUrl?: string
  estimatedPreparationTime?: number
  remainingTime?: number
  preparationStartedAt?: string
  readyAt?: string
  createdAt: string
  menuTheme?: string
}

const statusSteps = ['SUBMITTED', 'CONFIRMED', 'PREPARING', 'READY']

const statusConfig = {
  CART: {
    label: 'Carrello',
    icon: Package,
    description: 'Ordine in preparazione'
  },
  SUBMITTED: {
    label: 'Inviato',
    icon: Utensils,
    description: 'Il tuo ordine è stato ricevuto'
  },
  CONFIRMED: {
    label: 'Confermato',
    icon: CheckCircle,
    description: 'Il ristorante ha confermato'
  },
  PREPARING: {
    label: 'In Preparazione',
    icon: ChefHat,
    description: 'La cucina sta preparando il tuo ordine'
  },
  READY: {
    label: 'Pronto!',
    icon: CheckCircle,
    description: 'Il tuo ordine è pronto per il ritiro!'
  },
  DELIVERED: {
    label: 'Consegnato',
    icon: CheckCircle,
    description: 'Ordine completato'
  },
  CANCELLED: {
    label: 'Annullato',
    icon: Package,
    description: 'Ordine annullato'
  }
}

// Main component
export const OrderTrackingPage = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [theme, setTheme] = useState<MenuTheme>(MENU_THEMES.classic)

  useEffect(() => {
    loadOrder()
    const interval = setInterval(loadOrder, 5000)
    return () => clearInterval(interval)
  }, [orderId])

  const loadOrder = async () => {
    try {
      const response = await fetch(`/api/public/order/${orderId}`)
      if (!response.ok) throw new Error('Ordine non trovato')

      const data = await response.json()
      setOrder(data)
      setError(null)

      // Set theme from order if available
      if (data.menuTheme) {
        setTheme(getTheme(data.menuTheme as MenuThemeId))
      }
    } catch (err) {
      setError('Impossibile caricare l\'ordine')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const calculateRemainingTime = (): number | null => {
    if (!order?.estimatedPreparationTime) return null
    if (order.readyAt) return 0
    if (!order.preparationStartedAt) return order.estimatedPreparationTime

    const startTime = new Date(order.preparationStartedAt).getTime()
    const now = Date.now()
    const elapsedMinutes = Math.floor((now - startTime) / (1000 * 60))
    const remaining = order.estimatedPreparationTime - elapsedMinutes

    return Math.max(0, remaining)
  }

  const getProgressPercentage = (): number => {
    if (!order?.estimatedPreparationTime) return 0
    if (order.readyAt) return 100
    if (!order.preparationStartedAt) return 0

    const startTime = new Date(order.preparationStartedAt).getTime()
    const now = Date.now()
    const elapsedMinutes = Math.floor((now - startTime) / (1000 * 60))
    const progress = (elapsedMinutes / order.estimatedPreparationTime) * 100

    return Math.min(100, Math.max(0, progress))
  }

  const getCurrentStepIndex = () => {
    if (!order) return 0
    const index = statusSteps.indexOf(order.status)
    return index >= 0 ? index : 0
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 mx-auto mb-4 border-4 border-t-transparent rounded-full"
            style={{ borderColor: theme.textSecondary, borderTopColor: 'transparent' }}
          />
          <p style={{ color: theme.textSecondary }}>Caricamento ordine...</p>
        </motion.div>
      </div>
    )
  }

  // Error State
  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-8 max-w-md w-full text-center"
          style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.borderColor}` }}
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.textColor }}>Ordine Non Trovato</h2>
          <p className="mb-6" style={{ color: theme.textSecondary }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 text-white font-semibold rounded-xl transition-all hover:opacity-90"
            style={{ background: theme.buttonGradient }}
          >
            Torna alla Home
          </button>
        </motion.div>
      </div>
    )
  }

  const currentStatus = statusConfig[order.status as keyof typeof statusConfig] || statusConfig.SUBMITTED
  const StatusIcon = currentStatus.icon
  const remainingTime = calculateRemainingTime()
  const progress = getProgressPercentage()
  const currentStepIndex = getCurrentStepIndex()

  // Determine status color based on order state
  const getStatusColor = () => {
    if (order.status === 'READY') return '#22c55e' // green
    if (order.status === 'PREPARING') return theme.primaryColor
    return theme.textSecondary
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor }}>
      {/* Header - Clean and minimal */}
      <div className="sticky top-0 backdrop-blur-xl z-10" style={{ backgroundColor: `${theme.backgroundColor}E8`, borderBottom: `1px solid ${theme.borderColor}` }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
              style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.borderColor}` }}
            >
              <ArrowLeft className="w-5 h-5" style={{ color: theme.textColor }} />
            </button>
            {order.restaurantLogoUrl && order.restaurantLogoUrl.length > 0 ? (
              <img
                src={getAssetUrl(order.restaurantLogoUrl)}
                alt={order.restaurantName}
                className="w-10 h-10 rounded-full object-cover"
                style={{ border: `1px solid ${theme.borderColor}` }}
                onError={(e) => {
                  // Hide broken image and show fallback
                  e.currentTarget.style.display = 'none'
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className="w-10 h-10 rounded-full items-center justify-center text-lg font-bold"
              style={{
                backgroundColor: theme.cardBackground,
                color: theme.textColor,
                border: `1px solid ${theme.borderColor}`,
                display: order.restaurantLogoUrl && order.restaurantLogoUrl.length > 0 ? 'none' : 'flex'
              }}
            >
              {order.restaurantName.charAt(0)}
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-bold" style={{ color: theme.textColor }}>{order.restaurantName}</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Tavolo {order.tableNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Status Card - Elegant design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.borderColor}` }}
        >
          {/* Status Header */}
          <div className="p-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: order.status === 'READY'
                  ? 'rgba(34, 197, 94, 0.15)'
                  : order.status === 'PREPARING'
                    ? `${theme.primaryColor}20`
                    : theme.cardBackground,
                border: `2px solid ${getStatusColor()}`
              }}
            >
              <StatusIcon className="w-8 h-8" style={{ color: getStatusColor() }} />
            </motion.div>

            <h2 className="text-xl font-bold mb-1" style={{ color: theme.textColor }}>{currentStatus.label}</h2>
            <p className="text-sm" style={{ color: theme.textSecondary }}>{currentStatus.description}</p>
          </div>

          {/* Progress Steps - Subtle and clean */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between relative">
              {/* Progress Line Background */}
              <div className="absolute top-3 left-6 right-6 h-[2px]" style={{ backgroundColor: theme.borderColor }} />

              {/* Progress Line Filled */}
              <div className="absolute top-3 left-6 right-6 h-[2px]">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStepIndex / (statusSteps.length - 1)) * 100}%` }}
                  transition={{ duration: 0.5 }}
                  className="h-full"
                  style={{ backgroundColor: order.status === 'READY' ? '#22c55e' : theme.primaryColor }}
                />
              </div>

              {statusSteps.map((step, index) => {
                const stepConfig = statusConfig[step as keyof typeof statusConfig]
                const isCompleted = index < currentStepIndex
                const isCurrent = index === currentStepIndex
                const isUpcoming = index > currentStepIndex

                return (
                  <div key={step} className="relative flex flex-col items-center z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: isCompleted || isCurrent
                          ? (order.status === 'READY' && isCurrent ? '#22c55e' : theme.primaryColor)
                          : theme.cardBackground,
                        border: isUpcoming ? `2px solid ${theme.borderColor}` : 'none'
                      }}
                    >
                      {isCompleted ? (
                        <Check className="w-3 h-3 text-white" />
                      ) : isCurrent ? (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.textSecondary }} />
                      )}
                    </motion.div>
                    <span className="text-[10px] mt-2 font-medium" style={{
                      color: isCompleted || isCurrent ? theme.textColor : theme.textSecondary
                    }}>
                      {stepConfig.label.split(' ')[0]}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Preparation Progress - Only when preparing */}
          {order.status === 'PREPARING' && remainingTime !== null && (
            <div className="px-6 pb-6">
              <div className="rounded-xl p-4" style={{ backgroundColor: `${theme.primaryColor}10`, border: `1px solid ${theme.primaryColor}30` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium" style={{ color: theme.textColor }}>Tempo stimato</span>
                  <span className="font-bold flex items-center gap-1.5" style={{ color: theme.primaryColor }}>
                    <Clock className="w-4 h-4" />
                    {remainingTime} min
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: theme.borderColor }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: theme.primaryColor }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Ready Message */}
          {order.status === 'READY' && (
            <div className="px-6 pb-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-xl p-5 text-center"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
              >
                <motion.span
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="text-3xl block mb-2"
                >
                  🎉
                </motion.span>
                <p className="font-semibold text-lg" style={{ color: '#22c55e' }}>
                  Il tuo ordine è pronto!
                </p>
                <p className="text-sm mt-1" style={{ color: 'rgba(34, 197, 94, 0.8)' }}>
                  Puoi ritirarlo al banco
                </p>
              </motion.div>
            </div>
          )}
        </motion.div>

        {/* Order Details - Clean list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.borderColor}` }}
        >
          <div className="px-5 py-3" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
            <h3 className="font-semibold" style={{ color: theme.textColor }}>Dettagli Ordine</h3>
          </div>

          <div>
            {order.items.map((item, index) => (
              <div
                key={item.id}
                className="px-5 py-3"
                style={{ borderBottom: index < order.items.length - 1 ? `1px solid ${theme.borderColor}30` : 'none' }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: theme.textColor }}>
                      <span className="opacity-60">{item.quantity}×</span> {getItemName(item)}
                    </p>
                    {item.customizations && typeof item.customizations === 'object' && (
                      <div className="mt-1 space-y-0.5">
                        {item.customizations.selectedOptions?.map((option: any, idx: number) => (
                          <div key={idx} className="text-xs" style={{ color: theme.textSecondary }}>
                            {option.optionGroupName}: {option.optionName}
                            {option.priceModifier !== 0 && (
                              <span className={option.priceModifier > 0 ? 'text-green-400' : 'text-red-400'}>
                                {' '}({option.priceModifier > 0 ? '+' : ''}€{option.priceModifier.toFixed(2)})
                              </span>
                            )}
                          </div>
                        ))}
                        {item.customizations.freeTextNotes && (
                          <p className="text-xs italic" style={{ color: theme.textSecondary }}>"{item.customizations.freeTextNotes}"</p>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-sm" style={{ color: theme.textSecondary }}>€{item.itemTotal.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          {order.notes && (
            <div className="px-5 py-3" style={{ borderTop: `1px solid ${theme.borderColor}30` }}>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                <p className="text-sm" style={{ color: '#fbbf24' }}>
                  <span className="font-medium">Note:</span> {order.notes}
                </p>
              </div>
            </div>
          )}

          {/* Totals - Subtle */}
          <div className="px-5 py-4" style={{ backgroundColor: `${theme.backgroundColor}`, borderTop: `1px solid ${theme.borderColor}` }}>
            <div className="flex justify-between items-center">
              <span className="font-medium" style={{ color: theme.textSecondary }}>Totale</span>
              <span className="text-lg font-bold" style={{ color: theme.textColor }}>€{order.total.toFixed(2)}</span>
            </div>
          </div>
        </motion.div>

        {/* Payment & Customer Info - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl p-4"
          style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.borderColor}` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.backgroundColor }}>
                {order.paymentMethod === 'CASH' ? (
                  <Banknote className="w-5 h-5 text-green-400" />
                ) : (
                  <CreditCard className="w-5 h-5 text-blue-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-sm" style={{ color: theme.textColor }}>
                  {order.paymentMethod === 'CASH' ? 'Contanti' :
                   order.paymentMethod === 'CARD' || order.paymentMethod === 'STRIPE' ? 'Carta' :
                   order.paymentMethod === 'SATISPAY' ? 'Satispay' : 'Da definire'}
                </p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  {order.customerName} • #{order.id.slice(-6).toUpperCase()}
                </p>
              </div>
            </div>
            <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              order.paymentStatus === 'PAID'
                ? 'bg-green-500/15 text-green-400'
                : 'bg-amber-500/15 text-amber-400'
            }`}>
              {order.paymentStatus === 'PAID' ? 'Pagato' : 'Da pagare'}
            </div>
          </div>
        </motion.div>

        {/* Back to Menu Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => {
              const tableLink = localStorage.getItem('qorder_table_link')
              if (tableLink) {
                navigate(tableLink)
              } else {
                navigate(-1)
              }
            }}
            className="w-full py-3.5 font-medium rounded-xl transition-all hover:opacity-80"
            style={{
              backgroundColor: theme.cardBackground,
              border: `1px solid ${theme.borderColor}`,
              color: theme.textColor
            }}
          >
            Torna al Menu
          </button>
        </motion.div>

        {/* Footer - Powered by Qorder */}
        <div className="pt-4 pb-8">
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs" style={{ color: theme.textSecondary }}>Powered by</span>
            <Logo size={16} variant="icon" />
            <span className="text-xs font-semibold" style={{ color: theme.textSecondary }}>Qorder</span>
          </div>
        </div>
      </div>
    </div>
  )
}
